import { put, takeEvery, call } from 'redux-saga/effects';

import * as apis from '../../network/apis/challenges';
import {
  Challenge,
  ChallengeUser,
  ChallengeUsersAnalytics,
  ChallengeReward,
  ChallengeRewardsAnalytics,
} from '../../models/challenge.model';
import {
  getChallengesListRequest,
  getChallengesListResponse,
  getChallengeDetailsResponse,
  getChallengeUsersRequest,
  getChallengeUsersResponse,
  getChallengeRewardsResponse,
  getChallengeDetailsRequest,
  getChallengeRewardsRequest,
} from './slice';
import { ROUTES_PATHS } from '../../utils/RoutesPaths';
import { showSnackMsg } from '../../utils/Helpers';
import { CHALLENGE_STATUS } from '../../utils/Constants';
import {
  clearSkeletonObject,
  setCanShowEmptyState,
  setSkeletonObject,
} from '../Loader/slice';



export function* getChallengesListSaga({ payload }) {
  try {
    yield put(getChallengesListResponse({ challenges: [], meta: {} }));
    yield put(setSkeletonObject({ field: 'challenges', value: true }));
    const response = yield call(apis.getChallenges, payload);
    const challenges = Challenge.setChallenges(response.data?.quick_challenges);
    const meta = {
      currentPage: +response.headers['current-page'],
      count: +response.headers['total-count'],
      pages: +response.headers['total-pages'],
    };
    yield put(getChallengesListResponse({ challenges, meta }));
    yield put(setSkeletonObject({ field: 'challenges', value: false }));
    yield put(setCanShowEmptyState(challenges.length === 0));
  } catch (err) {
    yield put(setSkeletonObject({ field: 'challenges', value: false }));
    yield put(setCanShowEmptyState(true));
    yield showSnackMsg(err.response?.data?.message);
  }
}

export function* getChallengeDetailsSaga({ payload }) {
  try {
    const { id, fromForm } = payload;
    yield put(setSkeletonObject({ field: 'challenges', value: true }));
    // The form pre-fill (edit/duplicate) uses the global request loader instead
    // of the details-page skeleton, so drop the skeleton flag in that case.
    if (fromForm) yield put(clearSkeletonObject());
    const response = yield call(apis.getChallengeDetails, id);
    const challenge = Challenge.setChallenge(response.data?.quick_challenge);
    yield put(getChallengeDetailsResponse(challenge));
    yield put(setSkeletonObject({ field: 'challenges', value: false }));
    yield put(setCanShowEmptyState(!challenge));
  } catch (err) {
    yield put(setSkeletonObject({ field: 'challenges', value: false }));
    yield put(setCanShowEmptyState(true));
    yield showSnackMsg(err.response?.data?.message);
  }
}

export function* createChallengeSaga({ payload }) {
  try {
    yield call(apis.createChallenge, payload.data);
    yield showSnackMsg(payload.successMsg, 'success');
    yield payload.navigate(ROUTES_PATHS.challenges);
  } catch (err) {
    yield showSnackMsg(err.response?.data?.message);
  }
}

export function* editChallengeSaga({ payload }) {
  try {
    const { id, data, navigate, successMsg, listParams } = payload;
    yield call(apis.editChallenge, { id, data });
    yield showSnackMsg(successMsg, 'success');
    if (navigate) {
      yield navigate(ROUTES_PATHS.challenges);
    } else {
      yield put(getChallengesListRequest(listParams));
    }
  } catch (err) {
    yield showSnackMsg(err.response?.data?.message);
  }
}

export function* publishChallengeSaga({ payload }) {
  try {
    const { id, data, successMsg, fromList, listParams } = payload;
    yield call(apis.publishChallenge, { id, data });
    yield showSnackMsg(successMsg, 'success');
    if (fromList) {
      yield put(getChallengesListRequest(listParams));
    } else {
      yield put(getChallengeDetailsRequest({ id }));
    }
  } catch (err) {
    yield showSnackMsg(err.response?.data?.message);
  }
}

export function* duplicateChallengeSaga({ payload }) {
  try {
    const { id, navigate, successMsg } = payload;
    const response = yield call(apis.duplicateChallenge, id);
    yield showSnackMsg(successMsg, 'success');
    // Navigate to edit the newly created draft
    yield navigate(`${ROUTES_PATHS.challenges}/${response.data?.challenge?.id}/edit`);
  } catch (err) {
    yield showSnackMsg(err.response?.data?.message);
  }
}

export function* saveChallengeAsDraftSaga({ payload }) {
  try {
    yield call(apis.saveChallengeAsDraft, payload.data);
    yield showSnackMsg(payload.successMsg, 'success');
    yield payload.navigate(ROUTES_PATHS.challenges);
  } catch (err) {
    yield showSnackMsg(err.response?.data?.message);
  }
}

export function* deleteDraftSaga({ payload }) {
  try {
    const { id, successMsg, listParams } = payload;
    yield call(apis.deleteDraft, id);
    yield showSnackMsg(successMsg, 'success');
    yield put(getChallengesListRequest(listParams));
  } catch (err) {
    yield showSnackMsg(err.response?.data?.message);
  }
}

export function* getChallengeUsersSaga({ payload }) {
  try {
    const { params } = payload;
    yield put(setSkeletonObject({ field: 'usersTable', value: true }));

    const response = yield call(apis.getChallengeUsers, params);
    const meta = {
      currentPage: +response.headers['current-page'],
      count: +response.headers['total-count'],
      pages: +response.headers['total-pages'],
    };
    const completions = response.data?.quick_challenge_completions ?? [];
    yield put(
      getChallengeUsersResponse({
        users: ChallengeUser.setChallengeUsers(completions),
        meta,
        analytics: ChallengeUsersAnalytics.setChallengeUsersAnalytics(
          response.data?.users_list_analytics
        ),
      })
    );
    yield put(setSkeletonObject({ field: 'usersTable', value: false }));
  } catch (err) {
    yield put(setSkeletonObject({ field: 'usersTable', value: false }));
    yield showSnackMsg(err.response?.data?.message);
  }
}

// Bulk-submits the audited winner selection for reward disbursement.
export function* submitChallengeSelectionSaga({ payload }) {
  try {
    const { id, data, successMsg } = payload;
    yield call(apis.submitChallengeSelection, { id, data });
    yield showSnackMsg(successMsg, 'success');
    yield put(getChallengeDetailsRequest({ id }));
    yield put(getChallengeUsersRequest({ params: { quick_challenge_id: id, page: 1 } }));
    // TODO: BE confirm GET /challenges/:id/rewards returns the newly-submitted
    yield put(getChallengeRewardsRequest({ params: { quick_challenge_id: id, page: 1 } }));
  } catch (err) {
    yield showSnackMsg(err.response?.data?.message);
  }
}

export function* getChallengeRewardsSaga({ payload }) {
  try {
    const { params } = payload;
    yield put(setSkeletonObject({ field: 'rewardsTable', value: true }));

    const response = yield call(apis.getChallengeRewards, params);
    const meta = {
      currentPage: +response.headers['current-page'],
      count: +response.headers['total-count'],
      pages: +response.headers['total-pages'],
    };
    yield put(
      getChallengeRewardsResponse({
        rewards: ChallengeReward.setChallengeRewards(
          response.data?.quick_challenge_rewards ?? []
        ),
        meta,
        analytics: ChallengeRewardsAnalytics.setChallengeRewardsAnalytics(
          response.data?.rewards_list_analytics
        ),
      })
    );
    yield put(setSkeletonObject({ field: 'rewardsTable', value: false }));
  } catch (err) {
    yield put(setSkeletonObject({ field: 'rewardsTable', value: false }));
    yield showSnackMsg(err.response?.data?.message);
  }
}

export default function* ChallengesSagas() {
  yield takeEvery('challenges/getChallengesListRequest', getChallengesListSaga);
  yield takeEvery('challenges/getChallengeDetailsRequest', getChallengeDetailsSaga);
  yield takeEvery('challenges/createChallengeRequest', createChallengeSaga);
  yield takeEvery('challenges/editChallengeRequest', editChallengeSaga);
  yield takeEvery('challenges/publishChallengeRequest', publishChallengeSaga);
  yield takeEvery('challenges/duplicateChallengeRequest', duplicateChallengeSaga);
  yield takeEvery('challenges/saveChallengeAsDraftRequest', saveChallengeAsDraftSaga);
  yield takeEvery('challenges/deleteDraftRequest', deleteDraftSaga);
  yield takeEvery('challenges/getChallengeUsersRequest', getChallengeUsersSaga);
  yield takeEvery('challenges/submitChallengeSelectionRequest', submitChallengeSelectionSaga);
  yield takeEvery('challenges/getChallengeRewardsRequest', getChallengeRewardsSaga);
}
